import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Button, Alert, Badge, Modal, Table, ButtonGroup } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toast } from 'react-toastify';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { Pie } from 'react-chartjs-2';
import { 
  faFolder, 
  faTrash, 
  faSpinner, 
  faFileUpload,
  faFile,
  faFilePdf,
  faFileWord,
  faFileAlt,
  faArrowLeft,
  faEye,
  faCalendarAlt,
  faSearch,
  faThLarge,
  faList,
  faCheckCircle,
  faLightbulb,
  faExclamationCircle,
  faInfoCircle,
  faCalendarCheck
} from '@fortawesome/free-solid-svg-icons';
// Import API services
import { projectsAPI, documentsAPI } from '../../services/api';

// Custom styles for document cards and list
const documentStyles = `
  .hover-effect {
    transition: transform 0.2s, box-shadow 0.2s;
  }
  
  .hover-effect:hover {
    transform: translateY(-3px);
    box-shadow: 0 .5rem 1rem rgba(0,0,0,.15)!important;
  }
  
  .document-icon-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
  }
  
  .document-name {
    font-weight: 500;
  }
  
  .tag-container {
    transition: all 0.2s;
  }
  
  .tag-badge, .confidence-pill {
    transition: all 0.2s;
  }
  
  .tag-badge:hover, .confidence-pill:hover {
    filter: brightness(0.9);
  }
  
  .status-badge {
    font-size: 0.75rem;
    font-weight: normal;
  }
  
  .action-button {
    transition: all 0.2s;
    border-radius: 4px;
  }
  
  .action-button-icon {
    transition: all 0.2s;
    border-radius: 4px;
    width: 32px;
    height: 32px;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  .document-row {
    transition: background-color 0.2s;
  }
  
  .document-row:hover {
    background-color: rgba(0,0,0,0.02);
  }
  
  .document-table th {
    font-weight: 600;
    color: #495057;
  }
  
  .file-type-badge {
    display: inline-block;
    padding: 0.25rem 0.5rem;
    background-color: #f8f9fa;
    border-radius: 4px;
    font-size: 0.75rem;
    color: #495057;
    border: 1px solid #dee2e6;
  }
`;

const ProjectDetail = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  
  const [project, setProject] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [viewMode, setViewMode] = useState('list'); // 'grid' or 'list'
  const [analysisLoading, setAnalysisLoading] = useState({});

  useEffect(() => {
    fetchProjectDetails();
  }, [projectId]);

  const fetchProjectDetails = async () => {
    setLoading(true);
    setError('');
    
    try {
      // Fetch project details
      const projectResponse = await projectsAPI.getProject(projectId);
      
      if (projectResponse.data.status === 200 && projectResponse.data.data.project) {
        setProject(projectResponse.data.data.project);
      } else {
        setError('Project not found');
        return;
      }
      
      // Fetch project documents
      const documentsResponse = await documentsAPI.getDocuments(1, 10, '', projectId);
      
      if (documentsResponse.data.status === 200) {
        // Process documents to extract top tags from analysis_json
        const processedDocuments = (documentsResponse.data.data.documents || []).map(doc => {
          let topTags = [];
          
          // Check if document has analysis_json and tag_matches
          if (doc.analyzed && doc.analysis_json && doc.analysis_json.tag_matches) {
            try {
              // If analysis_json is a string, parse it
              const analysisData = typeof doc.analysis_json === 'string' 
                ? JSON.parse(doc.analysis_json) 
                : doc.analysis_json;
              
              // Extract and sort tag matches by confidence
              if (analysisData.tag_matches && Array.isArray(analysisData.tag_matches)) {
                topTags = analysisData.tag_matches
                  .sort((a, b) => b.confidence - a.confidence)
                  .slice(0, 3)
                  .map(tag => ({
                    name: tag.tag,
                    confidence: tag.confidence
                  }));
              }
            } catch (err) {
              console.error('Error parsing document analysis JSON:', err);
            }
          }
          
          // Return document with top tags
          return {
            ...doc,
            top_tags: topTags
          };
        });
        
        setDocuments(processedDocuments);
      }
    } catch (err) {
      console.error('Error fetching project details:', err);
      setError('Failed to load project details. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDocumentClick = (document) => {
    setDocumentToDelete(document);
    setShowDeleteModal(true);
  };

  const handleDeleteDocumentConfirm = async () => {
    if (!documentToDelete) return;
    
    setDeleteLoading(true);
    
    try {
      const response = await documentsAPI.deleteDocument(documentToDelete.id);
      
      if (response.data.status === 200) {
        // Remove deleted document from state
        setDocuments(documents.filter(d => d.id !== documentToDelete.id));
        setShowDeleteModal(false);
      } else {
        setError('Failed to delete document');
      }
    } catch (err) {
      console.error('Error deleting document:', err);
      setError('Failed to delete document. Please try again later.');
    } finally {
      setDeleteLoading(false);
    }
  };
  
  // Handle starting document analysis
  const handleStartAnalysis = async (documentId) => {
    try {
      // Set loading state for this specific document
      setAnalysisLoading(prev => ({ ...prev, [documentId]: true }));
      
      // Show initial toast notification with enhanced styling
      const toastId = toast.info(
        <div className="d-flex align-items-center">
          <div className="spinner-border spinner-border-sm text-info me-2" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <div>
            <strong className="text-primary">Step 1/4:</strong> Fetching data from document...
          </div>
        </div>, 
        {
          autoClose: false,
          position: 'top-right',
          className: 'toast-with-progress',
          progressClassName: 'progress-bar-animated progress-bar-striped bg-info',
          icon: '🔍'
        }
      );
      
      // Update toast after a delay to simulate progress
      setTimeout(() => {
        toast.update(toastId, {
          render: (
            <div className="d-flex align-items-center">
              <div className="spinner-grow spinner-grow-sm text-warning me-2" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <div>
                <strong className="text-warning">Step 2/4:</strong> Analyzing document content...
              </div>
            </div>
          ),
          autoClose: false,
          className: 'toast-with-progress',
          progressClassName: 'progress-bar-animated progress-bar-striped bg-warning',
          icon: '📄'
        });
      }, 1500);
      
      // Update toast again to show more progress
      setTimeout(() => {
        toast.update(toastId, {
          render: (
            <div className="d-flex align-items-center">
              <div className="spinner-border spinner-border-sm text-primary me-2" 
                   style={{borderWidth: '3px'}} role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <div>
                <strong className="text-primary">Step 3/4:</strong> Generating AI insights...
              </div>
            </div>
          ),
          autoClose: false,
          className: 'toast-with-progress',
          progressClassName: 'progress-bar-animated progress-bar-striped bg-primary',
          icon: '🤖'
        });
      }, 3000);
      
      // Call the analysis API endpoint
      console.log('Starting analysis for document ID:', documentId);
      const response = await documentsAPI.requestAnalysis(documentId);
      
      if (response.data.status === 200) {
        // Update the document in the state to show it as analyzed
        setDocuments(documents.map(doc => {
          if (doc.id === documentId) {
            return { ...doc, analyzed: true };
          }
          return doc;
        }));
        
        // Show success message and final toast update
        setTimeout(() => {
          toast.update(toastId, {
            render: (
              <div className="d-flex align-items-center">
                <div className="me-2">
                  <FontAwesomeIcon icon={faCheckCircle} className="text-success" style={{fontSize: '1.2rem'}} />
                </div>
                <div>
                  <strong className="text-success">Step 4/4:</strong> Analysis complete!
                  <div className="small">Document insights are ready to view</div>
                </div>
              </div>
            ),
            type: 'success',
            autoClose: 5000,
            className: 'toast-success-custom',
            progressClassName: 'bg-success',
            icon: '✅'
          });
          
          // Reload project data to show updated analysis results
          fetchProjectDetails();
        }, 4500);
        
        setError(''); // Clear any previous errors
      } else {
        // Show error toast
        toast.update(toastId, {
          render: (
            <div className="d-flex align-items-center">
              <div className="me-2 text-danger">
                <FontAwesomeIcon icon={faExclamationCircle} className="text-danger" style={{fontSize: '1.2rem'}} />
              </div>
              <div>
                <strong className="text-danger">Error:</strong> Failed to analyze document
                <div className="small">{response.data.message || 'Unknown error occurred'}</div>
              </div>
            </div>
          ),
          type: 'error',
          autoClose: 5000,
          className: 'toast-error-custom',
          progressClassName: 'bg-danger',
          icon: '❌'
        });
        
        setError('Failed to start document analysis: ' + response.data.message);
      }
    } catch (err) {
      console.error('Error starting document analysis:', err);
      setError('Failed to start document analysis. Please try again later.');
    } finally {
      // Clear loading state for this document
      setAnalysisLoading(prev => ({ ...prev, [documentId]: false }));
    }
  };

  if (loading) {
    return (
      <Container className="text-center py-5">
        <FontAwesomeIcon icon={faSpinner} spin size="3x" className="text-primary mb-3" />
        <h3>Loading project details...</h3>
      </Container>
    );
  }

  if (!project) {
    return (
      <Container className="text-center py-5">
        <Alert variant="danger">
          Project not found or you don't have permission to access it.
        </Alert>
        <Button as={Link} to="/projects" variant="primary" className="mt-3">
          <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
          Back to Projects
        </Button>
      </Container>
    );
  }

  // Helper function to get appropriate color for tag confidence
  const getConfidenceColor = (confidence) => {
    if (confidence >= 80) return 'success';
    if (confidence >= 60) return 'info';
    if (confidence >= 40) return 'primary';
    if (confidence >= 20) return 'danger';
    return 'danger';
  };

  // Format file size to human-readable format
  const formatFileSize = (bytes) => {
    if (!bytes) return 'Unknown size';
    
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    if (bytes === 0) return '0 Byte';
    const i = parseInt(Math.floor(Math.log(bytes) / Math.log(1024)));
    return Math.round(bytes / Math.pow(1024, i), 2) + ' ' + sizes[i];
  };

  // Apply custom styles - moved to top level to avoid conditional hook call


  return (
    <Container fluid className="py-4">
      {loading ? (
        <Container className="text-center py-5">
          <FontAwesomeIcon icon={faSpinner} spin size="3x" className="text-primary mb-3" />
          <h3>Loading project details...</h3>
        </Container>
      ) : !project ? (
        <Container className="text-center py-5">
          <Alert variant="danger">
            Project not found or you don't have permission to access it.
          </Alert>
          <Button as={Link} to="/projects" variant="primary" className="mt-3">
            <FontAwesomeIcon icon={faFolder} className="text-primary me-2" />
            Back to Projects
          </Button>
        </Container>
      ) : (
        <Container>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h2>
                <FontAwesomeIcon icon={faFolder} className="text-primary me-2" />
                {project.name}
              </h2>
              <p className="text-muted">
                <FontAwesomeIcon icon={faCalendarAlt} className="me-2" />
                Created: {new Date(project.created_at).toLocaleDateString()}
              </p>
            </div>
            <div>
              <Button as={Link} to="/projects" variant="outline-secondary" className="me-2">
                <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
                Back to Projects
              </Button>

              <Button as={Link} to={`/documents/upload`} variant="primary">
                <FontAwesomeIcon icon={faFileUpload} className="me-2" />
                Upload Document
              </Button>
            </div>
          </div>

          {/* Project Description */}
          {project.description && (
            <Card className="mb-4">
              <Card.Body>
                <Card.Title>Description</Card.Title>
                <Card.Text>{project.description}</Card.Text>
              </Card.Body>
            </Card>
          )}

          {/* Documents Section */}
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h3>Documents</h3>
            <ButtonGroup>
              <Button 
                variant={viewMode === 'grid' ? 'primary' : 'outline-primary'} 
                onClick={() => setViewMode('grid')}
              >
                <FontAwesomeIcon icon={faThLarge} />
              </Button>
              <Button 
                variant={viewMode === 'list' ? 'primary' : 'outline-primary'} 
                onClick={() => setViewMode('list')}
              >
                <FontAwesomeIcon icon={faList} />
              </Button>
            </ButtonGroup>
          </div>
          
          {documents.length === 0 ? (
            <Alert variant="info">
              No documents found in this project. Use the "Upload Document" button to add documents.
            </Alert>
          ) : viewMode === 'grid' ? (
            <Row xs={1} md={2} lg={3} className="g-4">
              {documents.map(document => (
                <Col key={document.id}>
                  <Card className="h-100 shadow-sm hover-effect">
                <Card.Header className="bg-light d-flex justify-content-between align-items-center">
                  <div className="text-truncate d-flex align-items-center">
                    <div className="document-icon-wrapper me-2">
                      <FontAwesomeIcon 
                        icon={document.file_type?.includes('pdf') ? faFilePdf : document.file_type?.includes('doc') ? faFileWord : faFile} 
                        className={`${document.file_type?.includes('pdf') ? 'text-danger' : document.file_type?.includes('doc') ? 'text-primary' : 'text-secondary'}`} 
                      />
                    </div>
                    <div className="text-truncate">
                      {document.filename || document.file_name || 'Untitled Document'}
                    </div>
                  </div>
                  <Badge 
                    bg={document.analyzed ? 'success' : 'warning'} 
                    className="ms-2 status-badge"
                    pill
                  >
                    {document.analyzed ? 'Analyzed' : 'Not Analyzed'}
                  </Badge>
                </Card.Header>
                <Card.Body>
                  <div className="mb-3">
                    <div className="d-flex align-items-center mb-2">
                      <FontAwesomeIcon icon={faCalendarCheck} className="text-muted me-2" size="sm" />
                      <small className="text-muted">Uploaded: {new Date(document.created_at).toLocaleDateString()}</small>
                    </div>
                    <div className="d-flex align-items-center mb-2">
                      <FontAwesomeIcon icon={faFileAlt} className="text-muted me-2" size="sm" />
                      <small className="text-muted">Size: {formatFileSize(document.file_size)}</small>
                    </div>
                    <div className="d-flex align-items-center">
                      <FontAwesomeIcon icon={faInfoCircle} className="text-muted me-2" size="sm" />
                      <small className="text-muted">Type: {document.file_type || 'Unknown'}</small>
                    </div>
                  </div>
                  
                  {document.analyzed && document.top_tags && document.top_tags.length > 0 && (
                    <div className="mb-3">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <div className="d-flex align-items-center">
                          <FontAwesomeIcon icon={faLightbulb} className="text-warning me-2" />
                          <h6 className="mb-0">Policy Tags</h6>
                        </div>
                        <Badge bg="primary" pill className="px-2">{document.top_tags.length} tags</Badge>
                      </div>
                      {document.top_tags.slice(0, 3).map((tag, index) => (
                        <div key={index} className="mb-2 tag-container">
                          <div className="d-flex justify-content-between align-items-center">
                            <div className="d-flex w-100 mb-1 tag-wrapper">
                              <Badge 
                                bg={getConfidenceColor(tag.confidence)}
                                className="text-truncate rounded-start border-0 tag-name" 
                                style={{ 
                                  fontWeight: 'bold',
                                  borderTopRightRadius: 0,
                                  borderBottomRightRadius: 0,
                                  flexGrow: 1,
                                  maxWidth: '70%',
                                  padding: '0.4rem 1rem'
                                }}
                              >
                                <span className="text-truncate">{tag.name}</span>
                              </Badge>
                              <span 
                                className="rounded-end px-2 d-inline-block text-center confidence-badge" 
                                style={{
                                  backgroundColor: '#f8f9fa',
                                  border: '1px solid #dee2e6',
                                  borderLeft: 0,
                                  color: '#495057',
                                  fontWeight: 'bold',
                                  minWidth: '45px',
                                  padding: '0.4rem 0.4rem'
                                }}
                              >
                                {Math.round(tag.confidence)}%
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </Card.Body>
                <Card.Footer className="bg-white border-top-0">
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      {document.analyzed ? (
                        <Button 
                          as={Link} 
                          to={`/documents/${document.id}`} 
                          variant="primary" 
                          size="sm"
                          className="me-2 action-button"
                        >
                          <FontAwesomeIcon icon={faEye} className="me-1" />
                          View Analysis
                        </Button>
                      ) : (
                        <Button 
                          variant="success" 
                          size="sm"
                          className="me-2 action-button"
                          onClick={() => handleStartAnalysis(document.id)}
                          disabled={analysisLoading[document.id]}
                        >
                          {analysisLoading[document.id] ? (
                            <>
                              <FontAwesomeIcon icon={faSpinner} spin className="me-1" />
                              Processing...
                            </>
                          ) : (
                            <>
                              <FontAwesomeIcon icon={faSearch} className="me-1" />
                              Start Analysis
                            </>
                          )}
                        </Button>
                      )}
                    </div>
                    <div>
                      <Button 
                        variant="danger" 
                        size="sm"
                        className="action-button-icon"
                        onClick={() => handleDeleteDocumentClick(document)}
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </Button>
                    </div>
                  </div>
                </Card.Footer>
              </Card>
            </Col>
          ))}
        </Row>
      ) : (
        <Card className="shadow-sm">
          <Card.Body className="p-0">
            <Table responsive hover className="mb-0 document-table">
              <thead className="bg-light">
                <tr>
                  <th className="ps-3">Document</th>
                  <th>Type</th> 
                  <th>Uploaded</th>
                  <th>Status</th>
                  <th className="text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {documents.map(document => (
                  <tr key={document.id} className="document-row">
                    <td className="ps-3">
                      <div className="d-flex align-items-center">
                        <div className="document-icon-wrapper me-2">
                          <FontAwesomeIcon 
                            icon={document.file_type?.includes('pdf') ? faFilePdf : document.file_type?.includes('doc') ? faFileWord : faFile} 
                            className={`${document.file_type?.includes('pdf') ? 'text-danger' : document.file_type?.includes('doc') ? 'text-primary' : 'text-secondary'}`} 
                          />
                        </div>
                        <div>
                          <span className="document-name">
                            {document.filename || document.file_name || 'Untitled Document'}
                          </span>
                          <div className="mt-1">
                            <small className="text-muted">{formatFileSize(document.file_size)}</small>
                          </div>
                        </div>
                      </div>
                      {/* Display top tags in list view - improved styling */}
                      {document.analyzed && document.top_tags && document.top_tags.length > 0 && (
                        <div className="mt-2 d-flex align-items-center flex-wrap">
                          <div className="d-flex align-items-center me-2 mb-1">
                            <FontAwesomeIcon icon={faLightbulb} className="text-warning me-1" size="sm" />
                            <small className="text-muted me-1">Tags:</small>
                          </div>
                          {document.top_tags.map((tag, index) => (
                            <div key={index} className="d-inline-flex align-items-center me-2 mb-1">
                              <Badge 
                                bg={getConfidenceColor(tag.confidence)} 
                                className="rounded-start border-0 tag-badge"
                                style={{ 
                                  fontSize: '0.75rem',
                                  fontWeight: 'normal',
                                  borderTopRightRadius: 0,
                                  borderBottomRightRadius: 0,
                                  paddingRight: '6px'
                                }}
                              >
                                {tag.name}
                              </Badge>
                              <span 
                                className="rounded-end px-2 d-inline-block confidence-pill" 
                                style={{
                                  fontSize: '0.75rem',
                                  backgroundColor: '#f8f9fa',
                                  border: '1px solid #dee2e6',
                                  borderLeft: 0,
                                  color: '#495057',
                                  fontWeight: 'bold'
                                }}
                              >
                                {Math.round(tag.confidence)}%
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </td>
                    <td>
                      <span className="file-type-badge">{document.file_type || 'Unknown'}</span>
                    </td>
                    <td>
                      <div className="d-flex align-items-center">
                        <FontAwesomeIcon icon={faCalendarCheck} className="text-muted me-2" size="sm" />
                        {new Date(document.created_at).toLocaleDateString()}
                      </div>
                    </td>
                    <td>
                      <Badge 
                        bg={document.analyzed ? 'success' : 'warning'}
                        pill
                        className="status-badge px-3 py-2"
                      >
                        {document.analyzed ? 'Analyzed' : 'Not Analyzed'}
                      </Badge>
                    </td>
                    <td className="text-center">
                      <div className="d-flex justify-content-center">
                        {document.analyzed ? (
                          <Button 
                            as={Link} 
                            to={`/documents/${document.id}`} 
                            variant="primary" 
                            size="sm"
                            className="me-2 action-button"
                            title="View Analysis"
                          >
                            <FontAwesomeIcon icon={faEye} />
                            View Analysis
                          </Button>
                        ) : (
                          <Button 
                            variant="success" 
                            size="sm"
                            className="me-2 action-button"
                            title={analysisLoading[document.id] ? "Analyzing..." : "Start Analysis"}
                            onClick={() => handleStartAnalysis(document.id)}
                            disabled={analysisLoading[document.id]}
                          >
                            {analysisLoading[document.id] ? (
                              <>
                                <FontAwesomeIcon icon={faSpinner} spin className="me-1" />
                                Processing...
                              </>
                            ) : (
                              <>
                                <FontAwesomeIcon icon={faSearch} className="me-1" />
                                Start Analysis
                              </>
                            )}
                          </Button>
                        )}
                        <Button 
                          variant="danger" 
                          size="sm"
                          className="action-button-icon"
                          title="Delete Document"
                          onClick={() => handleDeleteDocumentClick(document)}
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      )}
      
      {/* Delete Document Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {documentToDelete && (
            <p>Are you sure you want to delete the document <strong>{documentToDelete.filename || documentToDelete.file_name || 'Untitled Document'}</strong>? This action cannot be undone.</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)} disabled={deleteLoading}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeleteDocumentConfirm} disabled={deleteLoading}>
            {deleteLoading ? (
              <>
                <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                Deleting...
              </>
            ) : (
              'Delete Document'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
      </Container>
      )}
    </Container>
  );
};

export default ProjectDetail;
